import type { Brand, Device } from '@/models';

/**
 * Shared SSR-friendly helpers for the brand → model → products explorer.
 *
 * Filtering is deliberately static/in-memory: the server fetches the device
 * list once per navigation, then filters by text without extra DB queries.
 * The active `brand` + `search` live in the URL so pages are shareable and
 * indexable.
 */

export const ALL_BRANDS = 'all';
export const DEFAULT_BRAND_SLUG = 'samsung';
export const HOME_PREVIEW_LIMIT = 12;

/** Arabic-tolerant normalization so "أ" matches "ا", "ة" matches "ه", etc. */
export function normalizeArabicText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[ً-ٰٟ]/g, '')
    .replace(/ـ/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/\s+/g, ' ')
    .trim();
}

export function filterDevices(devices: Device[], search: string): Device[] {
  const query = normalizeArabicText(search);
  if (!query) {
    return devices;
  }
  const tokens = query.split(' ').filter(Boolean);
  return devices.filter((device) => {
    const haystack = normalizeArabicText(`${device.nameAr} ${device.nameEn ?? ''} ${device.slug}`);
    return tokens.every((token) => haystack.includes(token));
  });
}

/**
 * Resolve the effective brand from `?brand=`. Missing → Samsung default
 * (shareable, never an empty first paint). Unknown slugs → `all` rather
 * than an empty grid.
 */
export function resolveEffectiveBrand(brands: Brand[], requested?: string | null): string {
  const wanted = (requested ?? '').trim();
  if (!wanted) {
    return brands.some((brand) => brand.slug === DEFAULT_BRAND_SLUG) ? DEFAULT_BRAND_SLUG : ALL_BRANDS;
  }
  if (wanted === ALL_BRANDS) {
    return ALL_BRANDS;
  }
  return brands.some((brand) => brand.slug === wanted) ? wanted : ALL_BRANDS;
}

function withSearchParam(url: string, search: string): string {
  const query = search.trim();
  return query ? `${url}&search=${encodeURIComponent(query)}` : url;
}

/** Home explorer URL: `/?brand=<slug>&search=<text>` (brand always explicit). */
export function buildHomeUrl(brand: string, search: string): string {
  return withSearchParam(`/?brand=${encodeURIComponent(brand)}`, search);
}

export type DeviceLayout = 'grid' | 'list';

/** Full device catalog URL: `/devices?brand=&search=&layout=` (layout omitted when grid). */
export function buildDevicesUrl(brand: string, search: string, layout: DeviceLayout = 'grid'): string {
  const base = withSearchParam(`/devices?brand=${encodeURIComponent(brand)}`, search);
  return layout === 'list' ? `${base}&layout=list` : base;
}

/** Detail page for one model: `/products/device/<device-slug>`. */
export function buildDeviceUrl(deviceSlug: string): string {
  return `/products/device/${encodeURIComponent(deviceSlug)}`;
}
